import { useAuth } from './use-auth';
import { useOrg } from '@/store/org-context';
import { UserRole } from '@/types/api';

/**
 * Returns the user's role in the currently active organization.
 *
 * Owner-anywhere = Owner-everywhere: if the user holds Owner role on
 * their primary (JWT) org or in any membership entry, they're treated
 * as Owner regardless of which org is active. Mirrors the sysadmin pin
 * for non-sysadmin Owners — a user invited to another org as Manager
 * shouldn't be downgraded from their Owner status.
 *
 * Falls back to the active membership's role, then to the JWT role.
 */
export function useActiveRole(): UserRole {
  const { user } = useAuth();
  const { activeOrgId } = useOrg();

  if (!user) return UserRole.Viewer;

  const isOwnerAnywhere =
    user.role === UserRole.Owner ||
    (user.memberships?.some(m => m?.role === UserRole.Owner) ?? false);
  if (isOwnerAnywhere) return UserRole.Owner;

  const membership = user.memberships?.find(m => m?.organizationId === activeOrgId);
  if (membership) return membership.role;

  // Fallback to JWT role (primary org)
  return user.role;
}
